import { createRoot } from 'react-dom/client';

const roots = new WeakMap();

/**
 * Resolves a selector to an array of DOM elements.
 *
 * - Plain string (no prefix): resolved via getElementById — expects a unique element.
 * - '#id' prefix: resolved via querySelector — expects a unique element.
 * - '.class' prefix: resolved via querySelectorAll — returns all matching elements.
 *
 * Warns if no elements are found.
 *
 * @param containerSelector - plain ID, '#id', or '.class' selector string
 * @returns Element[]
 */
function getContainers(containerSelector) {
    const elements = containerSelector.startsWith('.')
        ? Array.from(document.querySelectorAll(containerSelector))
        : [containerSelector.startsWith('#')
            ? document.querySelector(containerSelector)
            : document.getElementById(containerSelector)
        ].filter(Boolean);

    if (!elements.length) {
        console.warn(`renderPage: no containers found for "${containerSelector}".`);
    }
    return elements;
}

/**
 * Renders a React element into one or more DOM containers.
 *
 * - Defers to DOMContentLoaded if the document is still loading.
 * - Caches React roots in a WeakMap to prevent double-mounting on re-calls.
 * - Class selectors render into every matching element; ID/plain selectors render into one.
 * - The factory is called once per container and receives the container element,
 *   allowing per-element data (e.g. data attributes) to be read inside it.
 *
 * @param containerSelector - plain ID, '#id', or '.class' selector string
 * @param factoryFunction - (container: Element) => ReactElement
 */
function renderPage(containerSelector, factoryFunction) {
    const mount = () => {
        const containers = getContainers(containerSelector);
        containers.forEach(container => {
            if (!roots.has(container)) {
                container.textContent = '';
                roots.set(container, createRoot(container));
            }
            roots.get(container).render(factoryFunction(container));
        });
    };

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', mount)
        : mount();
}

/**
 * Unmounts the React root(s) for the given selector and removes them from the cache.
 * A subsequent renderPage call for the same container will create a fresh root.
 *
 * @param containerSelector - plain ID, '#id', or '.class' selector string
 */
function unmountPage(containerSelector) {
    getContainers(containerSelector).forEach(container => {
        if (roots.has(container)) {
            roots.get(container).unmount();
            roots.delete(container);
        }
    });
}

export { unmountPage };
export default renderPage;
